import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AnnouncementBanner, BodyText, Button, Shimmer } from "../../../shared/ui";
import { useKybBusinessDetail } from "../../../shared/api/hooks/useKybBusinessDetail";
import {
  approveSubmittedKyb,
  escalateKybVerification,
  getBusinessCatalogue,
  updateKybBusinessStatus,
} from "../../../shared/api/kyb";
import { getApiErrorMessage } from "../../../shared/lib";
import KybComplianceShowBottomSection from "../components/KybComplianceShowBottomSection";
import KybComplianceActivityLogModal from "../components/KybComplianceActivityLogModal";
import KybComplianceEscalateModal from "../components/KybComplianceEscalateModal";
import KybComplianceFinishReviewModal from "../components/KybComplianceFinishReviewModal";
import KybComplianceRejectCustomerModal from "../components/KybComplianceRejectCustomerModal";
import KybComplianceShowTopSection from "../components/KybComplianceShowTopSection";
import KybComplianceRequestAdditionalInfoModal from "../components/KybComplianceRequestAdditionalInfoModal";
import KybComplianceRequestInformationUpdateModal from "../components/KybComplianceRequestInformationUpdateModal";
import { KYB_COMPLIANCE_DETAILS } from "../constants/details";
import { KYB_TABLE_ROWS } from "../constants/table";
import {
  KybComplianceRow,
  KybComplianceStatus,
  KybRequirement,
} from "../types";
import { PAGES } from "../../../shared/lib/pageUrlConstants";
import { getRequirementStatus } from "../components/kybShowReviewShared";
import { useMutation, useQuery } from "@tanstack/react-query";

type KybComplianceDetailsLocationState = {
  row?: KybComplianceRow;
  listStatus?: KybComplianceStatus;
  businessIds?: string[];
  informationRequestStatus?: string;
};

const attendedRequirementStatuses = ["APPROVED", "REJECTED", "QUERIED"];
const flaggedRequirementStatuses = ["REJECTED", "QUERIED"];

const getReviewableStatuses = (requirement: KybRequirement) => {
  const personStatuses = [
    ...(requirement.directors ?? []),
    ...(requirement.ubos ?? []),
  ]
    .map((person) => getRequirementStatus(person.status))
    .filter(Boolean);

  if (personStatuses.length > 0) return personStatuses;

  return [getRequirementStatus(requirement.status)].filter(Boolean);
};

const KybComplianceDetailsPage = () => {
  const [isFinishReviewModalOpen, setIsFinishReviewModalOpen] =
    useState(false);
  const [isActivityLogModalOpen, setIsActivityLogModalOpen] = useState(false);
  const [isEscalateModalOpen, setIsEscalateModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isRequestAdditionalModalOpen, setIsRequestAdditionalModalOpen] = useState(false);
  const [isRequestUpdateModalOpen, setIsRequestUpdateModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [updatingBusinessStatus, setUpdatingBusinessStatus] = useState<
    "APPROVED" | "REJECTED" | "QUERIED" | null
  >(null);
  const [isEscalating, setIsEscalating] = useState(false);
  const pageRef = useRef<HTMLElement | null>(null);
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState =
    (location.state as KybComplianceDetailsLocationState | null) ?? null;
  const rowFromState =
    locationState?.row ?? null;
  const listStatus = locationState?.listStatus;
  const businessIds = locationState?.businessIds ?? [];
  const currentBusinessIndex = businessIds.findIndex((businessId) => businessId === id);
  const previousBusinessId =
    currentBusinessIndex > 0 ? businessIds[currentBusinessIndex - 1] : null;
  const nextBusinessId =
    currentBusinessIndex >= 0 && currentBusinessIndex < businessIds.length - 1
      ? businessIds[currentBusinessIndex + 1]
      : null;

  const showExtraInfoRequestBtn = locationState?.listStatus === 'under-review' || locationState?.listStatus === 'approved';
  const isApprovedBusinesses = locationState?.listStatus === 'approved';

  const fallbackDetail = useMemo(() => {
    if (KYB_COMPLIANCE_DETAILS[id]) {
      return KYB_COMPLIANCE_DETAILS[id];
    }

    const row =
      rowFromState ??
      KYB_TABLE_ROWS.find((item) => item.id === id);

    if (!row) return null;

    return {
      ...row,
      statusLabel: row.status
        .split("-")
        .map((part) => part[0].toUpperCase() + part.slice(1))
        .join(" "),
      registeredContact: row.customerName,
      phoneNumber: "091 321 654 8901",
      phoneVerified: true,
      rcNumber: "012345",
      webPresence: "company.com",
      registeredAddress: "2323 S. Shepherd #1300, Houston, Texas, U...",
      physicalAddress: "2323 S. Shepherd #1300, Houston, Texas, U...",
      natureOfBusiness: "Business operations",
      isHighRisk: false,
      highRiskActivities: [],
      wallets: ["USD", "EUR"],
      proofOfAddressType: "Utility bill",
      transactionVolume: "-",
      sourceOfFunds: "-",
      directors: [],
      ubos: [],
      kycRequirements: [],
    };
  }, [id, rowFromState]);
  const { detail, isLoading, error, refetch } = useKybBusinessDetail(
    id,
    rowFromState,
  );

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    pageRef.current?.parentElement?.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [id]);

  const { mutateAsync: approveSubmittedKybMutation } = useMutation({
    mutationFn: () => approveSubmittedKyb(id),
    onSuccess: (res: any) => {
      if (res?.status === 400) {
        toast.error(
          getApiErrorMessage(res, "Unable to approve KYB information request."),
        );
      } else {
        toast.success("KYB information request approved successfully.");
        refetch();
      }
    },
    onError: (error) => {
      toast.error(
        getApiErrorMessage(error, "Unable to approve KYB information request."),
      );
    },
  });

  const resolvedDetail = detail ?? fallbackDetail;
  const shouldBlockShowPage =
    listStatus === "not-started" ||
    rowFromState?.status === "not-started" ||
    resolvedDetail?.status === "not-started";
  const isReadOnlyReview =
    resolvedDetail?.status === "approved" ||
    resolvedDetail?.status === "rejected" ||
    ["approved", "rejected"].includes(
      resolvedDetail?.statusLabel.trim().toLowerCase() ?? "",
    );

  useEffect(() => {
    if (!shouldBlockShowPage) return;

    navigate(PAGES.kybCompliance, { replace: true });
  }, [navigate, shouldBlockShowPage]);

  const disableDocumentAdd = shouldBlockShowPage || isReadOnlyReview;

  const { data: businessCatalogue, isLoading: businessCatalogueLoading } = useQuery({
    queryKey: ["business-catalogue", id],
    queryFn: () => getBusinessCatalogue(id),
    enabled: !!id,
  });

  const finishReviewState = useMemo(() => {
    const requirements = resolvedDetail?.kycRequirements ?? [];
    const hasRequirements = requirements.length > 0;
    const reviewableStatuses = requirements.flatMap(getReviewableStatuses);
    const pendingItemsCount = reviewableStatuses.filter(
      (status) => !attendedRequirementStatuses.includes(status),
    ).length;
    const flaggedItemsCount = reviewableStatuses.filter((status) =>
      flaggedRequirementStatuses.includes(status),
    ).length;

    const normalizedQueryStatus = (
      resolvedDetail?.queryStatus ??
      resolvedDetail?.informationRequestStatus ??
      ""
    )
      .toUpperCase()
      .trim();

    const isAwaitingCustomerResubmission =
      normalizedQueryStatus === "REQUESTED" ||
      (Boolean(resolvedDetail?.isQueried) && normalizedQueryStatus !== "SUBMITTED");

    return {
      canFinishReview:
        !isAwaitingCustomerResubmission &&
        hasRequirements &&
        reviewableStatuses.length > 0 &&
        pendingItemsCount === 0,
      flaggedItemsCount,
    };
  }, [resolvedDetail]);

  const navigateToBusiness = (businessId: string | null) => {
    if (!businessId) return;

    navigate(`${PAGES.kybCompliance}/${businessId}`, {
      state: {
        listStatus,
        businessIds,
      },
    });
  };

  const submitBusinessStatus = async (
    status: "APPROVED" | "REJECTED" | "QUERIED",
    reason?: string,
  ) => {
    if (!resolvedDetail) return;

    setUpdatingBusinessStatus(status);

    try {
      await updateKybBusinessStatus({
        businessId: resolvedDetail.id,
        status,
        reason,
      });
      toast.success(
        status === "APPROVED"
          ? "Customer approved successfully."
          : status === "REJECTED"
            ? "Customer rejected successfully."
            : "Changes requested successfully.",
      );
      setIsFinishReviewModalOpen(false);
      setIsRejectModalOpen(false);
      setRejectReason("");
      refetch();
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          status === "APPROVED"
            ? "Unable to approve customer."
            : status === "REJECTED"
              ? "Unable to reject customer."
              : "Unable to request changes.",
        ),
      );
    } finally {
      setUpdatingBusinessStatus(null);
    }
  };
  const submitEscalation = async ({
    recipients,
    role,
    roleName,
    note,
  }: {
    recipients?: Array<{ email: string; firstName: string }>;
    role?: string;
    roleName?: string;
    note?: string;
  }) => {
    if (!resolvedDetail || (!role && !recipients?.length)) return;

    setIsEscalating(true);

    try {
      if (role) {
        await escalateKybVerification({
          businessId: resolvedDetail.id,
          link: window.location.href,
          businessName: resolvedDetail.businessName,
          role,
          roleName,
          note,
        });
      } else {
        await Promise.all(
          (recipients ?? []).map((recipient) =>
            escalateKybVerification({
              businessId: resolvedDetail.id,
              link: window.location.href,
              adminEmail: recipient.email,
              businessName: resolvedDetail.businessName,
              adminFirstName: recipient.firstName,
              note,
            }),
          ),
        );
      }
      toast.success("KYB verification escalated successfully.");
      setIsEscalateModalOpen(false);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to escalate KYB verification."),
      );
    } finally {
      setIsEscalating(false);
    }
  };

  return (
    <section ref={pageRef} className="min-h-full bg-[#F3F4F8] md:p-6">
      <div className="rounded-[16px] bg-white overflow-hidden">
        {(detail?.queryStatus?.toUpperCase() === 'SUBMITTED' || detail?.informationRequestStatus === 'SUBMITTED') && (
          <AnnouncementBanner
            message="Customer has submitted the requested information"
            onDismiss={() => approveSubmittedKybMutation()}
          />
        )}
        <div className="p-4 md:p-6">
        {isLoading && !resolvedDetail ? (
          <div className="space-y-4">
            <Shimmer className="h-10 w-[180px] rounded-[10px]" />
            <Shimmer className="h-[220px] w-full rounded-[16px]" />
            <Shimmer className="h-[420px] w-full rounded-[16px]" />
          </div>
        ) : error && !resolvedDetail ? (
          <div className="space-y-4">
            <BodyText className="aku-body-large-regular text-[#C24E4E]">
              {error}
            </BodyText>
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-[12px]"
              onClick={refetch}
            >
              Retry
            </Button>
          </div>
        ) : resolvedDetail ? (
          <>
            <KybComplianceShowTopSection
              detail={resolvedDetail}
              canGoToPrevious={Boolean(previousBusinessId)}
              canGoToNext={Boolean(nextBusinessId)}
              canFinishReview={finishReviewState.canFinishReview}
              isReadOnly={isReadOnlyReview}
              onGoToPrevious={() => navigateToBusiness(previousBusinessId)}
              onGoToNext={() => navigateToBusiness(nextBusinessId)}
              onRejectCustomer={() => setIsRejectModalOpen(true)}
              onEscalateCustomer={() => setIsEscalateModalOpen(true)}
              onFinishReview={() => setIsFinishReviewModalOpen(true)}
              onOpenActivityLog={() => setIsActivityLogModalOpen(true)}
              onRequestAdditionalInfo={() => setIsRequestAdditionalModalOpen(true)}
              onRequestInformationUpdate={() => setIsRequestUpdateModalOpen(true)}
                  showExtraInfoRequestBtn={showExtraInfoRequestBtn}
                  isApprovedBusinesses={isApprovedBusinesses}
            />
            <KybComplianceShowBottomSection
              detail={resolvedDetail}
              disableDocumentAdd={disableDocumentAdd}
              isReadOnly={isReadOnlyReview}
              onRequirementReviewed={refetch}
            />
            <KybComplianceFinishReviewModal
              open={isFinishReviewModalOpen}
              businessName={resolvedDetail.businessName}
              flaggedItemsCount={finishReviewState.flaggedItemsCount}
              isSubmitting={
                updatingBusinessStatus === "APPROVED" ||
                updatingBusinessStatus === "QUERIED"
              }
              onOpenChange={setIsFinishReviewModalOpen}
              onConfirm={() =>
                submitBusinessStatus(
                  finishReviewState.flaggedItemsCount > 0
                    ? "QUERIED"
                    : "APPROVED",
                  finishReviewState.flaggedItemsCount > 0
                    ? "Some information requires changes."
                    : undefined,
                )
              }
            />
            <KybComplianceRejectCustomerModal
              open={isRejectModalOpen}
              reason={rejectReason}
              isSubmitting={updatingBusinessStatus === "REJECTED"}
              onOpenChange={(open) => {
                if (updatingBusinessStatus === "REJECTED") return;
                setIsRejectModalOpen(open);
                if (!open) setRejectReason("");
              }}
              onReasonChange={setRejectReason}
              onSubmit={() =>
                submitBusinessStatus("REJECTED", rejectReason)
              }
            />
            <KybComplianceActivityLogModal
              open={isActivityLogModalOpen}
              businessId={resolvedDetail.id}
              customerName={resolvedDetail.customerName}
              onOpenChange={setIsActivityLogModalOpen}
            />
            <KybComplianceEscalateModal
              open={isEscalateModalOpen}
              isSubmitting={isEscalating}
              onOpenChange={setIsEscalateModalOpen}
              onSubmit={submitEscalation}
            />
            <KybComplianceRequestAdditionalInfoModal
              open={isRequestAdditionalModalOpen}
              onOpenChange={setIsRequestAdditionalModalOpen}
                  detail={resolvedDetail}
                  businessCatalogue={businessCatalogue}
                  businessCatalogueLoading={businessCatalogueLoading}
                />
            <KybComplianceRequestInformationUpdateModal
              open={isRequestUpdateModalOpen}
              onOpenChange={setIsRequestUpdateModalOpen}
              detail={resolvedDetail}
                  businessCatalogue={businessCatalogue}
                  businessCatalogueLoading={businessCatalogueLoading}
            />
          </>
        ) : (
          <BodyText className="aku-body-large-regular text-[#383B56]">
            Customer record not found.
          </BodyText>
        )}
      </div>
      </div>
    </section>
  );
};

export default KybComplianceDetailsPage;
